import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { MapPin, Bed, Bath, Maximize, ArrowRight, Filter } from 'lucide-react';
import { propertyService } from '../services/propertyService';
import { toast } from 'sonner';

const Appartements = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    ville: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0
  });

  useEffect(() => {
    loadProperties();
  }, [filters, pagination.page]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const queryFilters = {
        typeBien: 'Appartement',
        page: pagination.page,
        limit: pagination.limit,
        actif: true
      };

      if (filters.search) queryFilters.search = filters.search;
      if (filters.ville !== 'all') queryFilters.ville = filters.ville;

      const response = await propertyService.getAllProperties(queryFilters);
      setProperties(response.data || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error('Error loading appartements:', error);
      toast.error('Erreur lors du chargement des appartements');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters({ ville: 'all', search: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1613490493576-7fde63acd811')`
          }}
        />
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Appartements</h1>
          <p className="text-xl max-w-2xl mx-auto text-slate-200">
            Découvrez notre sélection d'appartements au Sénégal
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="bg-slate-50 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Filtrer les appartements</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Input
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full"
              />
              <Select value={filters.ville} onValueChange={(value) => handleFilterChange('ville', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Ville" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  <SelectItem value="Dakar">Dakar</SelectItem>
                  <SelectItem value="Thiès">Thiès</SelectItem>
                  <SelectItem value="Saint-Louis">Saint-Louis</SelectItem>
                  <SelectItem value="Saly">Saly</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={resetFilters} variant="outline" className="w-full">
                Réinitialiser
              </Button>
            </div>
            <div className="mt-4 text-sm text-slate-600">
              {pagination.total} appartement{pagination.total > 1 ? 's' : ''} trouvé{pagination.total > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </section>

      {/* Villas Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <Card key={index} className="overflow-hidden border-0 shadow-lg">
                  <Skeleton className="h-64 w-full" />
                  <CardContent className="p-6 space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <div className="flex gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-8 w-1/3" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : properties.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {properties.map((property) => (
                  <Card key={property.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                    <div className="relative overflow-hidden h-64">
                      <img
                        src={property.images?.[0]?.urlImage || '/placeholder.jpg'}
                        alt={property.titre}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {property.statut === 'Disponible' && (
                        <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          Disponible
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-1">{property.titre}</h3>
                      <div className="flex items-center gap-2 text-slate-600 mb-4">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm line-clamp-1">
                          {property.localisation?.zone}, {property.localisation?.ville}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
                        {property.chambres && (
                          <div className="flex items-center gap-1">
                            <Bed className="w-4 h-4" />
                            <span>{property.chambres}</span>
                          </div>
                        )}
                        {property.sallesDeBain && (
                          <div className="flex items-center gap-1">
                            <Bath className="w-4 h-4" />
                            <span>{property.sallesDeBain}</span>
                          </div>
                        )}
                        {property.surface && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{property.surface}m²</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t pt-4 mb-4">
                        <div className="text-2xl font-bold text-amber-600">
                          {property.prix
                            ? `${parseInt(property.prix).toLocaleString()} ${property.devise || 'FCFA'}`
                            : 'Sur demande'
                          }
                        </div>
                      </div>

                      <Button asChild className="w-full bg-slate-900 hover:bg-slate-800">
                        <Link to={`/property/${property.id}`}>
                          Voir les détails
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {pagination.total > pagination.limit && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {pagination.page} sur {Math.ceil(pagination.total / pagination.limit)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🏘️</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Aucun appartement trouvé
              </h3>
              <p className="text-slate-600 mb-6">
                Essayez de modifier vos critères de recherche
              </p>
              <Button onClick={resetFilters} className="bg-amber-600 hover:bg-amber-700">
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Appartements;
